import React from 'react';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import classNames from 'classnames';

export type TrackingDisplayStyle =
  /** Describes the Apple Pay button shown on mobile for single-item cart bundles */
  'hoisted' |
  /** Describes the Apple Pay button always shown in a cart bundle footer when available */
  'default' |
  /** Describes the Apple Pay button shown in the Buy It Now modal when available */
  'buy-now';

export type ApplePayButtonVariant = 'black' | 'white-outlined';

interface Props {
  variant: ApplePayButtonVariant;
  hidden: boolean;
  shouldTrack: boolean;
  tracking: {
    applePayAvailableForDevice: boolean;
    displayStyle: TrackingDisplayStyle,
  };
  onClick: (e) => void;
}

const trackingComponentName = 'ApplePayButton';

export function ApplePayButton(props: Props) {
  const { fiveDigitLocale } = useUser();

  function onClick(e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    e.preventDefault();

    trackEvent({
      eventName: MParticleEventName.ClickedApplePayButton,
      displayStyle: props.tracking.displayStyle,
    });

    props.onClick(e);
  }

  useViewTracking({
    componentName: trackingComponentName,
    eventName: MParticleEventName.ApplePayOffered,
    ...props.tracking,
  }, props.shouldTrack);

  if (props.hidden) {
    return null;
  }

  const classes = classNames('apple-pay-button',
    {
      'apple-pay-button--black': props.variant === 'black',
      'apple-pay-button--white-outlined': props.variant === 'white-outlined',
    },
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyPress={onClick}
      className={classes}
      lang={fiveDigitLocale}
    >
    </div>
  );
}
